import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, IconButton, Box, Container, Card, CardHeader, Divider, CardContent, TextField, Grid, CardActions, MenuItem, ListItemText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BackIcon from '@mui/icons-material/ChevronLeft';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Formik } from 'formik';
import ConfirmButton from '../components/ConfirmButton';
import SubmitFormButton from '../components/SubmitFormButton';
import PreCheckQuestionsService from '../services/PreCheckQuestionsService';
import AppConstants from '../constants/AppConstants';
import { hide, show } from '../store/actions/busyIndicatorActions';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import { formatSuccessMessage, formatErrorMessage } from '../utils/common';
import { PreCheckQuestionRes } from '../proxy/proxy';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
}

const PrecheckQuestionDetail: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator
}) => {
  const { id } = useParams();
  const questionId = !Number.isNaN(Number(id)) ? Number(id) : 0;
  const navigate = useNavigate();
  const [questionData, setQuestionData] = useState<PreCheckQuestionRes | null>(null);

  const refreshData = () => {
    showBusyIndicator();
    clearAllAlerts();
    PreCheckQuestionsService.getQuestionById(questionId)
      .then((result) => {
        setQuestionData(result);
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      })
      .then(() => {
        hideBusyIndicator();
      });
  };

  useEffect(() => {
    if (questionId) {
      refreshData();
    } else {
      setQuestionData({});
    }
  }, []);

  const handleFormSubmit = (values: any) => {
    showBusyIndicator();
    clearAllAlerts();

    const payload = {
      question: values.question.trim(),
      isBlocker: values.isBlocker === 'true',
      requiresPhoto: values.requiresPhoto === 'true',
      pageNumber: Number(values.pageNumber)
    };

    const promise = questionId ? PreCheckQuestionsService.updateQuestion(questionId, payload) : PreCheckQuestionsService.createQuestion(payload);

    promise
      .then((response) => {
        showAlert(formatSuccessMessage(questionId ? 'Question updated successfully' : 'Question created successfully'));
        setQuestionData(null);
        setQuestionData(response);
        if (!questionId) {
          navigate(`/app/shift/question/${response.id}`, { replace: true });
        }
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      }).then(() => {
        hideBusyIndicator();
      });
  };

  const back = () => {
    navigate(-1);
  };

  const handleDeleteClick = () => {
    showBusyIndicator();
    clearAllAlerts();
    PreCheckQuestionsService.deleteQuestion(questionId)
      .then(() => {
        showAlert(formatSuccessMessage('Question deleted successfully'));
        back();
      }).catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      })
      .then(() => {
        hideBusyIndicator();
      });
  };

  return (
    <>
      <Helmet>
        <title>Question Details | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          <Card>
            <CardHeader
              avatar={
                (
                  <IconButton aria-label="back" size="small" onClick={back}>
                    <BackIcon />
                  </IconButton>
                )
              }
              title="Question Details"
              action={
                !!questionId && (
                  <ConfirmButton
                    buttonIcon={
                      (
                        <DeleteIcon />
                      )
                    }
                    isIconButton
                    dialogTitle="Are you sure?"
                    showCancelButton
                    message="Precheck question will be deleted permanently."
                    buttonColor="secondary"
                    onConfirm={handleDeleteClick}
                  />
                )
              }
            />
            <Divider />
            <CardContent>
              {
                !!questionData && (
                  <Formik
                    initialValues={{
                      question: questionData.question || '',
                      isBlocker: questionData.isBlocker ? 'true' : 'false',
                      requiresPhoto: questionData.requiresPhoto ? 'true' : 'false',
                      pageNumber: questionData.pageNumber || 0
                    }}
                    validationSchema={Yup.object().shape({
                      question: Yup.string().max(255).required('Question is required')
                    })}
                    onSubmit={handleFormSubmit}
                  >
                    {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      touched,
                      values,
                      dirty,
                      isValid
                    }) => (
                      <form onSubmit={handleSubmit}>
                        <CardContent>
                          <Grid container spacing={3}>
                            <Grid
                              item
                              md={12}
                              xs={12}
                            >
                              <TextField
                                error={Boolean(touched.question && errors.question)}
                                fullWidth
                                helperText={touched.question && errors.question}
                                label="Question"
                                margin="normal"
                                name="question"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.question}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                multiline
                              />
                            </Grid>
                            <Grid item md={4} xs={12}>
                              <TextField
                                error={Boolean(
                                  touched.pageNumber && errors.pageNumber
                                )}
                                fullWidth
                                helperText={touched.pageNumber && errors.pageNumber}
                                label="Page Number"
                                margin="normal"
                                name="pageNumber"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.pageNumber}
                                variant="outlined"
                                type="number"
                                InputLabelProps={{ shrink: true }}
                                multiline
                              />
                            </Grid>
                            <Grid item md={4} xs={12}>
                              <TextField
                                fullWidth
                                label="Is Photo required?"
                                name="requiresPhoto"
                                margin="normal"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                select
                                InputLabelProps={{ shrink: true }}
                                value={values.requiresPhoto}
                                variant="outlined"
                                error={Boolean(
                                  touched.requiresPhoto && errors.requiresPhoto
                                )}
                                helperText={touched.requiresPhoto && errors.requiresPhoto}
                              >
                                <MenuItem value="true">
                                  <ListItemText primary="Yes" />
                                </MenuItem>
                                <MenuItem value="false">
                                  <ListItemText primary="No" />
                                </MenuItem>
                              </TextField>
                            </Grid>
                            <Grid
                              item
                              md={4}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label="Is Blocker?"
                                name="isBlocker"
                                margin="normal"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                select
                                InputLabelProps={{ shrink: true }}
                                value={values.isBlocker}
                                variant="outlined"
                                error={Boolean(touched.isBlocker && errors.isBlocker)}
                                helperText={touched.isBlocker && errors.isBlocker}
                              >
                                <MenuItem value="true">
                                  <ListItemText primary="Yes" />
                                </MenuItem>
                                <MenuItem value="false">
                                  <ListItemText primary="No" />
                                </MenuItem>
                              </TextField>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button sx={{ marginRight: 2 }} color="secondary" onClick={back} variant="outlined">
                            Cancel
                          </Button>
                          <SubmitFormButton
                            dirty={dirty}
                            isValid={isValid}
                            editMode={Boolean(questionId)}
                            onConfirm={handleSubmit}
                          />
                        </CardActions>
                      </form>
                    )}
                  </Formik>
                )
              }
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = null;

const mapDispatchToProps = (dispatch: any) => ({
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts())
});

export default connect(mapStateToProps, mapDispatchToProps)(PrecheckQuestionDetail);
